import { memone } from '@deqo/foundation';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { component, DateType, field, ObjectManager, property } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { AutoBringToFront } from '../../canvas/entities/AutoBringToFront';
import { FixedRectangular } from '../../canvas/entities/FixedRectangular';
import { Physical, PotentialInteraction, RelativeTo } from '../../canvas/entities/Physical';
import { TextAlignment } from '../../canvas/entities/TextConventions';
import { FieldLayout } from '../../canvas/fields/AdaptableField';
import { SimpleTextField } from '../../canvas/fields/SimpleTextField';
import { GraffitiTarget } from '../../canvas/visualization/Graffiti';
import { TextRuler } from '../../canvas/visualization/TextRuler';
import { Audited } from '../auditing/Audited';
import { Concealable } from '../conceal/Concealable';
import { flip } from '../conceal/ConcealCommands';
import { Pingable } from '../presentation/Pingable';
import { Colors } from './../../../ui';

const invocation = defineCommand(
    'valueCardInvoke',
    'ValueCard: Invoke',
    (r, [id]: [string]) => {
        const dispatch = r.call(CommandDispatcher);
        dispatch(flip, [[id]]);
    },
    [],
);

const mutation = defineCommand(
    'valueCardMutate',
    'ValueCard: Mutate',
    (r, [id]: [string]) => {
        const objects = r.call(ObjectManager);
        const card = objects.getMaybe(id, ValueCard);
        if (!card || !!card.concealed) return;
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const valueCardDef = {
    name: 'ValueCard',
    implements: [Physical, AutoBringToFront, Audited, Concealable, Pingable, GraffitiTarget],
    invocation,
    mutation,
};

const valueCardTransformHints = Object.freeze({
    scaleX: false,
    scaleY: false,
    rotate: false,
    translate: true,
});

const dims = {
    margin: 4,
    small: {
        font: 12,
        line: 1.25,
        margin: [0, 2],
    },
    large: {
        // font: 56,
        line: 1,
        margin: 2,
    },
};

export interface ValueCardLayout {
    readonly width: number;
    readonly height: number;
    readonly inner: Rect;
    readonly small: Rect;
    readonly large: Rect;
}

export const ValueCardLayout = (object: ValueCard): ValueCardLayout => {
    const { margin } = dims;

    const width = object.style === 'regular' ? 140 : 140;
    const height = object.style === 'regular' ? 180 : 140;

    const inner = Rect.fromEdges(
        margin,
        margin,
        width - margin,
        height - margin
    );

    const textWidth = inner.width - (margin * 4);

    const smallField = object.field('smallText') as ValueCardSmallText;
    const smallHeight = TextRuler.measureHeight(smallField.value, smallField, textWidth);

    const small = Rect.fromEdges(
        inner.left + margin,
        inner.top + margin,
        inner.right - margin,
        inner.top + margin + smallHeight,
    );

    const largeField = object.field('largeText') as ValueCardLargeText;
    const largeHeight = TextRuler.measureHeight(largeField.value, largeField, textWidth);

    const large = Rect.fromEdges(
        inner.left + margin,
        inner.top + inner.center().y - (largeHeight / 2),
        inner.right - margin,
        inner.top + inner.center().y + (largeHeight / 2),
    );

    return { width, height, inner, small, large };
};

export class ValueCardSmallText extends SimpleTextField {

    public readonly fontFamily: string = 'Cabin';
    public readonly fontColor: string = Colors.textLight;
    public readonly fontWeight: number = 500;
    public readonly fontSize: number = dims.small.font;
    public readonly lineHeight: number = dims.small.line;
    public readonly textMargin: number | number[] = dims.small.margin;

    public get layout(): FieldLayout {
        return new FieldLayout(this.owner.cast(ValueCard).layout.small, 2);
    }
}

export class ValueCardLargeText extends SimpleTextField {

    public readonly fontFamily: string = 'Fira Sans';
    public readonly fontColor: string = 'inherit';
    public readonly fontWeight: number = 500;
    public readonly lineHeight: number = dims.large.line;
    public readonly textMargin: number | number[] = dims.large.margin;
    public readonly textAlignH = TextAlignment.middle;

    constructor() {
        super();
        Object.defineProperty(this, 'fontSize', {
            get: () => this.computeFontSize(this.value)
        });
    }

    public get default() {
        return true;
    }

    public get layout(): FieldLayout {
        return new FieldLayout(this.owner.cast(ValueCard).layout.large, 2);
    }

    @memone()
    private computeFontSize(value: string) {
        const unicodeLength = [...value].length;
        if (unicodeLength <= 3) return 56;
        if (unicodeLength <= 4) return 52;
        if (unicodeLength <= 5) return 42;
        return 0;
    }
}

@component(valueCardDef)
export class ValueCard extends FixedRectangular implements AutoBringToFront, Audited, Concealable {

    @property({ type: 'number', default: 140 })
    public width!: number;

    @property({ type: 'number', default: 180 })
    public height!: number;

    @property({ type: 'string', default: Colors.primary })
    public color!: string;

    @property({ type: 'string', default: 'regular' })
    public style!: string;

    @field({ type: ValueCardSmallText, valueOnly: true })
    public smallText!: string;

    @field({ type: ValueCardLargeText, valueOnly: true })
    public largeText!: string;

    @property({ type: 'number', default: 0 })
    public value!: number;

    @property({ type: 'number', default: null })
    public sort!: number;

    @property({ type: 'boolean', default: false })
    public concealed!: boolean;

    @property({ type: 'string' })
    public userId!: string;

    @property({ type: DateType })
    public created!: Date;

    public get boundary(): boolean {
        return true;
    }

    public get layout() {
        return this.computeLayout(this.width, this.height, this.smallText, this.largeText);
    }

    public area(to: RelativeTo | Physical = RelativeTo.Parent): Rect {
        return Rect.fromPoints(this.transform(to).apply(this.localBounds.inflate([0, 18]).offset([0, -18])));
    }

    public test(mpt: Point): boolean {
        const lpt = this.toRelative(mpt);
        if (this.localBounds.contains(lpt)) return true;
        if (this.localBounds.pointAt(.5, 0).distanceTo(lpt) <= 18) return true;
        return false;
    }

    public testInteraction({ type, mpt }: PotentialInteraction, _force?: boolean, _selected?: boolean): [boolean, string] {
        const lpt = this.toRelative(mpt);
        if (this.localBounds.pointAt(.5, 0).distanceTo(lpt) <= 18) return [type === 'invoke', 'flip'];
        if (this.localBounds.contains(lpt)) return [type !== 'invoke', 'default'];
        return [false, 'none'];
    }

    protected setTransformationResult(x: number, y: number, r: number, w: number, h: number): void {
        const newBounds = new Rect(x, y, w, h);
        const newCenter = newBounds.center();

        w = this.style === 'regular' ? 140 : 140;
        h = this.style === 'regular' ? 180 : 140;
        x = newCenter.x - (w / 2);
        y = newCenter.y - (h / 2);
        r = 0;

        super.setTransformationResult(x, y, r, w, h);
    }

    @memone()
    private computeLayout(..._memoTargets: any[]) {
        return ValueCardLayout(this);
    }
}

